import React, { useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import { Archive, Building2, RefreshCw } from 'lucide-react';
import axios from '../../services/AxiosInstance';
import { getApiUrl } from '../../utils/ApiUtils';
import AccountPage from '../layouts/AccountPage';
import PageHeader from '../layouts/PageHeader';
import EmptyState from '../components/Common/EmptyState';
import StatusBadge from '../components/Common/StatusBadge';
import Callout from '../components/Common/Callout';

const labels = { pending: 'Väntar', running: 'Pågår', done: 'Klart för denna del', review: 'Behöver handläggas', retained: 'Bevaras / följs upp' };
const stepTones = { pending: 'muted', running: 'info', done: 'success', review: 'warning', retained: 'info' };
const statuses = { cleanup_pending: 'Rensning pågår', review_required: 'Behöver handläggas', handled: 'Rensning klar' };
const statusTones = { cleanup_pending: 'info', review_required: 'warning', handled: 'success' };
const date = value => value ? new Date(value).toLocaleString('sv-SE') : 'Inte registrerat';

export default function CompanyClosures() {
    const [jobs, setJobs] = useState([]);
    const [busy, setBusy] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState('');
    const refresh = async () => {
        setBusy(true); setError('');
        try { setJobs((await axios.get(getApiUrl('auth/company-closures'))).data); }
        catch { setError('Företagsavsluten kunde inte hämtas. Försök igen.'); }
        finally { setBusy(false); setLoaded(true); }
    };
    useEffect(() => { refresh(); }, []);
    const retry = async id => {
        setBusy(true); setError('');
        try { await axios.post(getApiUrl(`auth/company-closures/${id}/retry`), {}); await refresh(); }
        catch (failure) {
            setError(failure?.response?.data?.code === 'RECENT_LOGIN_REQUIRED'
                ? 'Logga ut och logga in igen för att begära ett nytt försök.'
                : failure?.response?.data?.message || 'Ett nytt försök kunde inte begäras.');
        } finally { setBusy(false); }
    };
    return <AccountPage width="wide">
        <PageHeader icon={Archive} title="Företagsavslut" subtitle="Avslut du har begärt som ägare. Åtkomsten stängs direkt; rensningen följs upp här."
            actions={<Button variant="outline-primary" size="sm" onClick={refresh} disabled={busy}>
                <RefreshCw size={14} className={busy ? 'spin' : ''} aria-hidden="true" /> {busy ? 'Hämtar…' : 'Uppdatera status'}</Button>} />
        {error && <Callout tone="danger" role="alert"><p>{error}</p></Callout>}
        {loaded && !busy && !error && !jobs.length && <EmptyState icon={Archive} title="Inga företagsavslut"
            description="Du har inte begärt något företagsavslut. Ett avslut startas från företagsvalet eller från företagets inställningar." />}
        {jobs.map(job => <section key={job.closure_id} className="modern-card" aria-label={`Företagsavslut ${job.company_id}`}>
            <div className="modern-card-header">
                <div>
                    <h2 className="section-title"><Building2 aria-hidden="true" />{`Avslut begärt ${date(job.requested_at)}`}</h2>
                    <p className="section-subtitle">Företags-ID <span className="ll-code">{job.company_id}</span></p>
                </div>
                <div className="action-buttons"><StatusBadge tone={statusTones[job.status] || 'warning'}>{statuses[job.status] || 'Status behöver kontrolleras'}</StatusBadge></div>
            </div>
            <div className="modern-card-body">
                <ul className="ll-meta mb-3">
                    <li className="ll-meta__item"><Building2 size={14} aria-hidden="true" />{job.companyRemoved ? 'Företaget är borttaget från appen' : 'Företaget är stängt för användning'}</li>
                    <li className="ll-meta__item"><Archive size={14} aria-hidden="true" />{job.archive_delivery === 'server_flushed' ? 'Arkivexport skickad (mottagande ej verifierat)' : 'Ingen arkivexport registrerad'}</li>
                </ul>
                <div className="ll-table-card mb-3">
                    <table className="ll-table">
                        <thead><tr><th>Del</th><th>Status</th><th>Uppföljning</th></tr></thead>
                        <tbody>{job.steps.map(step => <tr key={step.step_key}>
                            <td>{step.label}</td>
                            <td><StatusBadge tone={stepTones[step.status] || 'muted'}>{labels[step.status] || step.status}</StatusBadge></td>
                            <td>{step.decision_reason || <span className="text-muted">—</span>}{step.retention_until && <div className="small text-muted">{`Bevaras till ${date(step.retention_until)}`}</div>}</td>
                        </tr>)}</tbody>
                    </table>
                </div>
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                    <p className="small text-muted mb-0">Rensningen sker automatiskt utan godkännande från Ledgerloop. Säkerhetskopior kan finnas kvar efter att rensningen är klar.</p>
                    {job.status !== 'handled' && <Button variant="outline-primary" size="sm" disabled={busy} onClick={() => retry(job.closure_id)}>Försök rensa igen</Button>}
                </div>
            </div>
        </section>)}
    </AccountPage>;
}
